import React from 'react';
import { SectionFigure } from './SectionFigure';
import { DayNightGraphic } from './DayNightGraphic';
import { DinnerChecklist } from './DinnerChecklist';
import { sectionImages } from '../data/sectionImages';
import { OFFER_URL, PRODUCT_COVER } from '../data/offer';
const headingClass = 'pt-4 font-serif text-[1.5rem] font-bold leading-snug tracking-tight text-ink sm:text-[1.7rem]';
const quoteClass = 'border-l-4 border-brand bg-paper py-3 pl-4 font-semibold italic';
function ListBlock({
  items
}: {
  items: string[];
}) {
  return <ul className="space-y-2 pl-1" data-id="element-26" data-mp="19lw4h36gy51e">
      {items.map(item => <li key={item} className="flex gap-3" data-id="element-27" data-mp="1nkckjkq817w7">
          <span className="mt-[0.65rem] h-1.5 w-1.5 shrink-0 rounded-full bg-brand" aria-hidden="true" data-id="element-28" data-mp="1nhzsgeqr1h5f" />
          <span data-id="element-29" data-mp="1in2jt8xdt9t2">{item}</span>
        </li>)}
    </ul>;
}
function OfferLink({
  children
}: {
  children: React.ReactNode;
}) {
  return <a href={OFFER_URL} className="font-semibold text-brand underline decoration-brand/40 underline-offset-4 transition-colors duration-150 ease-out hover:text-brand-dark hover:decoration-brand-dark" data-id="element-30" data-mp="2u0y1opfo7wnz">
      {children}
    </a>;
}
export function ArticleBody() {
  return <div className="mt-8 space-y-5 font-serif text-[1.08rem] leading-[1.75] text-ink sm:text-[1.14rem]" data-id="element-31" data-mp="18peu0gizeqsk">
      <DayNightGraphic data-id="element-32" data-mp="1dpaxmenrog7l" />

      <p data-id="element-33" data-mp="1dnpz6dyc0d4g">
        <span className="float-left mr-2 mt-1 font-serif text-[3.4rem] font-bold leading-[0.8] text-brand" data-id="element-34" data-mp="1do9le6v0lnoa">
          H
        </span>
        ay una frase que se repite cada vez más entre mujeres de más de 35 años:
      </p>
      <p className={quoteClass} data-id="element-35" data-mp="ky0xdydss91s">«Como sano, pero igual me inflamo.»</p>
      <p data-id="element-36" data-mp="pxvnhzpr0uls">
        No hablan de una comida pesada. No hablan de pizza, frituras o postres. Muchas veces hablan
        de una cena que, en teoría, debería ayudarlas: verduras, ensalada, yogur, pan integral, algo
        «liviano» para no acostarse con culpa.
      </p>
      <p data-id="element-37" data-mp="292qdzxe4gpl">Pero después ocurre lo mismo.</p>
      <p data-id="element-38" data-mp="27rne2brrczj">
        Se van a dormir con el abdomen tenso. Se despiertan con la cara pesada. Sienten gases,
        presión, cansancio o esa sensación de que el cuerpo no terminó de descansar.
      </p>
      <p data-id="element-39" data-mp="3ldepy8zg7w13">Y entonces aparece la pregunta más frustrante:</p>
      <p className={quoteClass} data-id="element-40" data-mp="3xy5b0l070na">«Si estoy haciendo todo bien… ¿por qué mi cuerpo responde tan mal?»</p>
      <p data-id="element-41" data-mp="3v6c27xdukor9">
        La respuesta podría estar en un lugar que casi nadie mira: la relación entre tu digestión
        nocturna, tu microbiota y la forma en que cenas después de los 35.
      </p>

      <h2 className={headingClass} data-id="element-42" data-mp="32hom0g7ygpkk">
        La microbiota no solo necesita «probióticos»: necesita que dejes de dañarla todas las noches
      </h2>
      <SectionFigure {...sectionImages.microbiota} data-id="element-43" data-mp="3hbt7m845apji" />
      <p data-id="element-44" data-mp="3cdrm7gj8ye4v">
        En los últimos años se habla mucho de flora intestinal, microbiota y probióticos. Pero hay
        una idea clave que suele perderse en el camino:
      </p>
      <p className="font-semibold" data-id="element-45" data-mp="rou26c4bgm1e">
        No alcanza con «agregar bacterias buenas» si cada noche repites hábitos que alimentan el
        desequilibrio.
      </p>
      <p data-id="element-46" data-mp="za63ww1g6pyc">
        La diversidad bacteriana intestinal funciona como un ecosistema. Cuanto más diverso y
        equilibrado está, mejor puede colaborar con procesos como digestión, inflamación, energía,
        sistema inmune y hasta estado de ánimo.
      </p>
      <p data-id="element-47" data-mp="ua1nt74gbx8n">
        El problema es que la vida moderna suele empujar en la dirección contraria: alimentos
        refinados, exceso de azúcar, estrés, mal sueño, sedentarismo, antibióticos mal acompañados,
        cenas improvisadas y horarios que no respetan el ritmo del cuerpo.
      </p>
      <p data-id="element-48" data-mp="1972b8yfm161b">Y cuando esa diversidad se pierde, el intestino puede volverse más sensible.</p>
      <p data-id="element-49" data-mp="1d7cmapi9emn1">
        Lo que antes «no hacía nada», ahora cae pesado. Lo que antes se digería fácil, ahora
        fermenta. Lo que antes era una cena normal, ahora se convierte en una noche incómoda.
      </p>

      <h2 className={headingClass} data-id="element-50" data-mp="25of2pmh892cj">
        El error silencioso: cenar como si tu cuerpo siguiera en modo día
      </h2>
      <SectionFigure {...sectionImages.clock} data-id="element-51" data-mp="i0n40kyt323r" />
      <p data-id="element-52" data-mp="d0qyuiedh4mm">
        Durante el día, tu cuerpo está más preparado para procesar alimentos variados. Hay más
        movimiento, más actividad metabólica, más demanda energética.
      </p>
      <p data-id="element-53" data-mp="83h8n0j5soxq">Pero por la noche, el organismo empieza a cambiar de prioridad.</p>
      <p data-id="element-54" data-mp="41vp4wc4dcek">
        Quiere bajar revoluciones. Quiere preparar el sueño. Quiere reparar tejidos. Quiere regular
        hormonas. Quiere descansar.
      </p>
      <p data-id="element-55" data-mp="vqqjmt36jxk8">
        El punto es que muchas cenas «saludables» no están diseñadas para ese momento biológico.
      </p>
      <p data-id="element-56" data-mp="qzgv5vtdb09x">
        Una gran cantidad de verduras crudas, un yogur frío con endulzantes, una tostada integral o
        una mezcla rápida «para no cocinar» pueden parecer livianas en el plato, pero exigir
        demasiado trabajo a un sistema digestivo que ya está entrando en modo descanso.
      </p>
      <p data-id="element-57" data-mp="1ep1mb1zod3ua">Y ahí aparece la paradoja:</p>
      <p className={quoteClass} data-id="element-58" data-mp="1eo048dk8rh32">
        La cena que elegiste para cuidarte puede ser la misma que mantiene a tu cuerpo trabajando
        mientras intentas dormir.
      </p>
      <p data-id="element-59" data-mp="32ch5w48qkxhd">
        Si quieres ver cómo se aplica este enfoque de forma práctica, puedes revisar aquí el{' '}
        <OfferLink data-id="element-60" data-mp="36c7uq2epd3j9">Método de Cenas Antiinflamatorias en 15 minutos</OfferLink>.
      </p>

      <h2 className={headingClass} data-id="element-61" data-mp="3be6wj7usnse3">
        Cuando las enzimas no alcanzan, la comida empieza a «quedarse» demasiado tiempo
      </h2>
      <SectionFigure {...sectionImages.digestion} data-id="element-62" data-mp="3lathrxr18lgn" />
      <p data-id="element-63" data-mp="3l9idzxxl7dpj">
        Una parte importante de la digestión depende de enzimas: sustancias que ayudan a descomponer
        proteínas, grasas e hidratos de carbono.
      </p>
      <p data-id="element-64" data-mp="3u3my7yi7tqw3">
        Cuando la digestión es eficiente, la comida avanza mejor. Pero cuando el sistema está lento,
        estresado o sobrecargado, ciertos alimentos pueden llegar al intestino sin estar
        suficientemente procesados.
      </p>
      <p className="font-semibold" data-id="element-65" data-mp="7w6m7m1rnaxb">¿Resultado?</p>
      <p data-id="element-66" data-mp="23yid4igifxxn">
        Fermentación. Gases. Distensión. Pesadez. Cambios en el ritmo intestinal. Sensación de
        «panza dura».
      </p>
      <p data-id="element-67" data-mp="1z1jlc6kbk32m">
        Y esto no siempre ocurre porque el alimento sea «malo». A veces ocurre porque llegó en el
        momento equivocado, en la forma equivocada o en una combinación que tu cuerpo nocturno no
        pudo manejar bien.
      </p>
      <p data-id="element-68" data-mp="2mpj8yxmc3dcz">Por eso tantas mujeres se sienten confundidas.</p>
      <ListBlock items={['Comen vegetales, pero se inflaman.', 'Evitan azúcar, pero tienen antojos.', 'Cenan poco, pero duermen mal.', 'Compran productos light, pero despiertan pesadas.']} data-id="element-69" data-mp="2mnyh5frem8it" />
      <p className="font-semibold" data-id="element-70" data-mp="2ro4cb5wvud5n">
        El problema no es falta de disciplina. El problema es falta de estrategia nocturna.
      </p>

      <h2 className={headingClass} data-id="element-71" data-mp="12a7wahppqwyy">La pregunta que cambia la cena después de los 35</h2>
      <SectionFigure {...sectionImages.choice} data-id="element-72" data-mp="xbwmu83byoh9" />
      <p data-id="element-73" data-mp="38xyh6dprn09e">La mayoría de las dietas pregunta:</p>
      <p className={quoteClass} data-id="element-74" data-mp="38yzw4t9wk6we">«¿Cuántas calorías tiene esto?»</p>
      <p data-id="element-75" data-mp="3dwrvg8cvmvam">Pero una mujer que se inflama por la noche debería empezar por otra pregunta:</p>
      <p className={quoteClass} data-id="element-76" data-mp="2z855lcpmil55">«¿Mi cuerpo puede digerir esto fácilmente mientras intenta descansar?»</p>
      <p data-id="element-77" data-mp="346ymlf44cua2">Esa pregunta cambia todo.</p>
      <p data-id="element-78" data-mp="345x8t2vpxton">
        Porque una cena adecuada para la noche no necesariamente es la más pequeña. Tampoco es la
        más «fit» o la más fría. Muchas veces es la más simple, tibia, cocida, saciante y amable con
        el intestino.
      </p>
      <p data-id="element-79" data-mp="2v47816fy5ie7">
        No se trata de demonizar las ensaladas, el pan integral o el yogur. Se trata de entender que
        después de cierta hora, y especialmente después de los 35, tu cuerpo puede necesitar otro
        tipo de señal.
      </p>
      <p data-id="element-80" data-mp="eho4wgl4kfzd">Una señal de calma. Una señal de digestión fácil. Una señal de reparación.</p>

      <h2 className={headingClass} data-id="element-81" data-mp="jhixqpognfn2">
        Qué tienen en común las cenas que mejor toleran muchas mujeres
      </h2>
      <DinnerChecklist data-id="element-82" data-mp="33c096ipnxoun" />
      <p data-id="element-83" data-mp="33c8la8dyoq4s">Las cenas nocturnas mejor diseñadas suelen compartir varios principios:</p>
      <ListBlock items={['Son tibias o calientes.', 'Incluyen alimentos cocidos y fáciles de asimilar.', 'Evitan grandes volúmenes de crudos.', 'Tienen proteína suficiente para dar saciedad.', 'Usan combinaciones simples.', 'Incorporan especias digestivas.', 'No requieren una hora de cocina.', 'Están pensadas para una mujer real, cansada, con poco tiempo.']} data-id="element-84" data-mp="2yf9sbt61ap86" />
      <p data-id="element-85" data-mp="2anunntqsbvtr">
        Ahí es donde muchas mujeres encuentran una diferencia: no en comer menos, sino en hacer que
        la cena deje de ser una carga para el cuerpo.
      </p>
      <p data-id="element-86" data-mp="8wzocrxddeia">
        Porque cuando el intestino no tiene que pasar la noche peleando con una comida difícil, el
        descanso cambia. La mañana cambia. La relación con el propio cuerpo también puede cambiar.
      </p>

      <h2 className={headingClass} data-id="element-87" data-mp="bhz2ybo2cem9">La recomendación final</h2>
      <figure className="my-7" data-id="element-88" data-mp="bfcu88pfrt2g">
        <a href={OFFER_URL} className="block overflow-hidden rounded-sm bg-brand-blush" aria-label="Ver el Método de Cenas Antiinflamatorias en 15 minutos" data-id="element-89" data-mp="gg8veeiel146">
          <img src={PRODUCT_COVER} alt="Portada digital de Cenas Anti-inflamatorias en una tablet sobre la mesada de la cocina" className="aspect-[16/9] w-full object-cover" loading="lazy" data-id="element-90" data-mp="l80oowsy1ky0" />
        </a>
        <figcaption className="mt-2 font-sans text-xs leading-relaxed text-ink-muted" data-id="element-91" data-mp="3tm94136ggf88">
          Método de Cenas Antiinflamatorias en 15 minutos, guía práctica en formato digital.
        </figcaption>
      </figure>
      <p data-id="element-92" data-mp="7gltzspobvb4">
        Si sientes que tu abdomen se inflama cada noche aunque intentes comer sano, quizás no
        necesitas otra dieta extrema.
      </p>
      <p data-id="element-93" data-mp="1iuzecnl7xsme">
        Quizás necesitas entender qué tipo de cena tu cuerpo puede procesar mejor cuando llega la
        noche.
      </p>
      <p data-id="element-94" data-mp="3li86vsg8inuj">
        Por eso, al final de esta nota, la recomendación editorial es revisar el{' '}
        <OfferLink data-id="element-95" data-mp="3pk39r1dtd9af">Método de Cenas Antiinflamatorias en 15 minutos</OfferLink>, una guía práctica
        pensada para mujeres de más de 35 que quieren cenar comida real, sin pasar hambre, sin
        ingredientes raros y sin volver a depender de la ensalada fría de siempre.
      </p>
    </div>;
}